import * as React from 'react';
import * as _ from 'underscore';

import {angularProviderHOC} from '../../../common/react/AngularProviderHOC';
import {AngularDirective} from '../../../common/react/components/AngularDirective';
import {ISettingsCoordinatorCtrlScope} from '../../../settings/directives/tbSettingsCoordinator';
import {ISiteSettingsForServerAdmin} from '../../../settings/types/ISite';
import {
  IAnalyticsExtSiteSettings,
  IDashExtSiteSettings, ISiteSettingsForSiteAdmin,
} from '../../../settings/types/ISiteSettingsForSiteAdmin';
import {AngularContext} from '../../../ts/factories/AngularContextFactory';
import {FeatureFlags} from '../../../ts/FeatureFlags';
import {ServerSettings, ServerSettingsInstance} from '../../../settings/services/ServerSettings';
import {Sites} from '../../../ts/siteManagement/services/resources/Sites';
import {IGetDashExtSiteSettingsResult} from '../../../ts/ServerApi/requests/settings/extensions/dashboard/GetExtensionsSiteSettingsRequest';
import {
  IDashExtServerSettings,
  IExtsvcServerSettings
} from '../../../ts/ServerApi/types/IEditableServerSettings';
import {SessionManagementService} from '../../../sessionManagement/SessionManagementService';
import {RouterError} from '../RouterHelpers/RouterError';
import {SpacesSiteUrls} from '../SpacesSiteUrls';
import {subspaceStyle} from '~src/settings/styles/CommonStyles';
import {ExtensionsSettingsCoordinator} from '~src/settings/general-settings/components/extensions-settings/ExtensionsSettingsCoordinator';

const template = `<div ng-include="'siteSettingsShouldShowExtensions.html'"></div>`;

export interface DashExtSiteSettingsSubspaceOwnProps {
  readonly isServerPage: boolean;
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly angularContext: AngularContext;
  readonly server: SessionManagementService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
}

export interface ExtensionsSiteSettingsSubspaceAngularProps {
  readonly serverSettingsService: ServerSettings;
  readonly sites: Sites;
}

export type ExtensionsSettingsSubspaceProps =
  DashExtSiteSettingsSubspaceOwnProps
  & ExtensionsSiteSettingsSubspaceAngularProps;

export interface ExtensionsSettingsSubspaceState {
  readonly isServerPage: boolean;
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly isMultiSite: boolean;
  readonly dashExtServerSettings: IDashExtServerSettings;
  readonly dashExtSiteSettings: IGetDashExtSiteSettingsResult;
  readonly siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
  readonly extsvcServerSettings: IExtsvcServerSettings;
  readonly analyticsExtensionsServerSettings: IExtsvcServerSettings;
  readonly analyticsExtensionsSiteSettings: IAnalyticsExtSiteSettings;
  readonly siteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin;
}

export class ExtensionsSiteSettingsSubspaceComponent extends React.PureComponent<ExtensionsSettingsSubspaceProps, ExtensionsSettingsSubspaceState> {

  private childScope: ISettingsCoordinatorCtrlScope<IDashExtSiteSettings> = undefined;
  private unmounted: boolean = false;

  private initialState: ExtensionsSettingsSubspaceState = {
    isServerPage: undefined,
    isMultiSite: undefined,
    hasDataToRender: false,
    hasError: false,
    dashExtServerSettings: undefined,
    dashExtSiteSettings: undefined,
    siteSettingsForServerAdmin: undefined,
    extsvcServerSettings: undefined,
    analyticsExtensionsServerSettings: undefined,
    analyticsExtensionsSiteSettings: undefined,
    siteSettingsForSiteAdmin: undefined,
  };

  public constructor(props: ExtensionsSettingsSubspaceProps) {
    super(props);
    this.state = {
      ...this.initialState,
      isServerPage: props.isServerPage,
      isMultiSite: props.isSessionMultiSite
    };
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: ExtensionsSettingsSubspaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    if (!_.isUndefined(this.state.dashExtServerSettings)
      && !_.isUndefined(this.state.dashExtSiteSettings)
      && !_.isUndefined(this.state.siteSettingsForServerAdmin)
      && (!_.isUndefined(this.state.siteSettingsForSiteAdmin) || !FeatureFlags.prepExtensionsEinstein)
      && (!_.isUndefined(this.state.analyticsExtensionsSiteSettings))
      && (!_.isUndefined(this.state.analyticsExtensionsServerSettings))
    ) {
      this.updateState<'hasDataToRender'>({hasDataToRender: true});
    }

    if (this.state.hasDataToRender && !_.isUndefined(this.childScope)) {
      _.defer(() => this.childScope.$apply());
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      if (this.props.urls instanceof SpacesSiteUrls) {
        return <RouterError errorType={'default'} urls={this.props.urls}/>;
      } else {
        return <RouterError errorType={'server-default'} urls={this.props.urls}/>;
      }
    }

    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div className={FeatureFlags.extensionsArmModeEnabled ? subspaceStyle : 'tb-stylish-settings'}>
        {FeatureFlags.extensionsArmModeEnabled
          ? <ExtensionsSettingsCoordinator {...this.state}/>
          : <AngularDirective
            template={template}
            angularContext={this.props.angularContext}
            setupScope={this.setupScope}
          />
        }
      </div>
    );
  }

  private updateState<K extends keyof ExtensionsSettingsSubspaceState>(newState: Pick<ExtensionsSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    this.getExtensionsServerSettings();
    this.getSiteSettings();
    this.getSiteSettingsForServerAdmin();
    if (FeatureFlags.prepExtensionsEinstein) {
      this.getSiteSettingsForSiteAdmin();
    }
    this.getAnalyticsExtensionsServerSettings();
    this.getAnalyticsExtensionsSiteSettings();
    if (FeatureFlags.extSvcSiteLevelSettingsEnabled) {
      this.getExtsvcServerSettings();
    }
  }

  private getExtensionsServerSettings = async () => {
    try {
      const dashExtServerSettings = await this.props.serverSettingsService.getExtensionsSettings();
      this.updateState({dashExtServerSettings: dashExtServerSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getExtsvcServerSettings = async () => {
    try {
      const extsvcServerSettings = await this.props.serverSettingsService.getExtsvcSettings();
      this.updateState({extsvcServerSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteSettings = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await this.props.sites.getCurrentExtensionsSiteSettingsForSiteAdmin();
      this.updateState({dashExtSiteSettings: siteSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteSettingsForServerAdmin = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const serverSettings = await this.props.sites.getCurrentSiteSettingsForServerAdmin();
      this.updateState({siteSettingsForServerAdmin: serverSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteSettingsForSiteAdmin = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await this.props.sites.getCurrentSiteSettingsForSiteAdmin();
      this.updateState<'siteSettingsForSiteAdmin'>({siteSettingsForSiteAdmin: siteSettings});
    } catch (e) {
      this.updateState<'hasError'>({hasError: true});
    }
  }

  private getAnalyticsExtensionsSiteSettings = async () => {
    try {
      let analyticsExtensionsSettings = await this.props.serverSettingsService.getAnalyticsSiteLevel();
      this.updateState({analyticsExtensionsSiteSettings: analyticsExtensionsSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getAnalyticsExtensionsServerSettings = async () => {
    try {
      let analyticsExtensionsSettings = await this.props.serverSettingsService.getAnalyticsServerLevel();
      this.updateState({analyticsExtensionsServerSettings: analyticsExtensionsSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as ISettingsCoordinatorCtrlScope<IDashExtSiteSettings>;
    childScope.showEncryptionStatus = true;
    childScope.server = this.props.server;
    childScope.hideNameAndId = !this.props.isSessionMultiSite;
    childScope.extensionsServerSettings = this.state.dashExtServerSettings;
    childScope.extsvcServerSettings = this.state.extsvcServerSettings;
    childScope.siteSettings = this.state.dashExtSiteSettings;
    childScope.siteSettingsForServerAdmin = this.state.siteSettingsForServerAdmin;
    this.childScope = childScope;
    return childScope;
  }
}

const ExtensionsSiteSettingsHOC = angularProviderHOC<DashExtSiteSettingsSubspaceOwnProps, ExtensionsSiteSettingsSubspaceAngularProps, ExtensionsSiteSettingsSubspaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    serverSettingsService: ServerSettingsInstance,
    sites: Sites.instance,
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const ExtensionsSettingsSubspace: React.ComponentType<DashExtSiteSettingsSubspaceOwnProps>
  = ExtensionsSiteSettingsHOC(ExtensionsSiteSettingsSubspaceComponent);
